
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Language } from '@/types';
import { Menu, X, ArrowRight } from 'lucide-react';

interface NovaNavbarProps {
  lang: Language;
}

const NovaNavbar: React.FC<NovaNavbarProps> = ({ lang }) => {
  const isRtl = lang === 'ar';
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);



  const navLinks = [
    { label: lang === 'en' ? 'Home' : 'الرئيسية', href: '/products/nova' },
    { label: lang === 'en' ? 'Features' : 'الميزات', href: '/products/nova#features' },
    { label: lang === 'en' ? 'Pricing' : 'الأسعار', href: '/nova/pricing' },
    { label: lang === 'en' ? 'Data' : 'البيانات', href: '/nova/library' },
    { label: lang === 'en' ? 'Docs' : 'الدليل', href: '/nova/docs' },
    { label: lang === 'en' ? 'Download' : 'التحميل', href: '/nova/download' },
    { label: lang === 'en' ? 'Changelog' : 'التحديثات', href: '/nova/changelog' },
    { label: lang === 'en' ? 'About' : 'حول', href: '/nova/about' },
  ];

  // Close menu on navigation
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <>
      <motion.nav
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="fixed top-0 left-0 right-0 z-[100] flex justify-between items-center px-6 lg:px-12 h-16 w-full bg-[#02040a]/90 backdrop-blur-md border-b border-white/[0.08] shadow-[0_4px_30px_rgba(0,0,0,0.5)]"
      >

        {/* BRAND / LOGO */}
        <div className="flex items-center gap-2 transition-all duration-300">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-1.5 h-6 bg-[#7F77DD] rounded-sm group-hover:shadow-[0_0_15px_rgba(127,119,221,0.8)] transition-all" />
            <span className="text-lg font-black tracking-tight text-white uppercase italic flex items-center gap-1.5">
              <span>VEXL NOVA</span>
              {location.pathname.startsWith('/nova/docs') && (
                <span className="text-[#7F77DD] drop-shadow-[0_0_8px_rgba(127,119,221,0.5)] font-black italic text-sm">DOCS</span>
              )}
            </span>
          </Link>
        </div>

        {/* DESKTOP LINKS */}
        <div className="hidden lg:flex items-center gap-10">
          {navLinks.map((link, i) => {
            const isExternal = link.href.startsWith('http');
            const isActive = location.pathname === link.href;
            const className = `text-[11px] font-bold uppercase tracking-widest transition-all ${
              isActive ? 'text-[#7F77DD] font-extrabold' : 'text-slate-400 hover:text-[#7F77DD]'
            }`;
            
            return isExternal ? (
              <a key={i} href={link.href} className={className}>
                {link.label}
              </a>
            ) : (
              <Link key={i} to={link.href} className={className}>
                {link.label}
              </Link>
            );
          })}
        </div>

        {/* ACTIONS / MOBILE TOGGLE */}
        <div className="flex items-center gap-3 md:gap-4">
          {/* GitHub Link */}
          <a
            href="https://github.com/vexl-gis"
            target="_blank"
            rel="noopener noreferrer"
            className="
              hidden sm:inline-flex items-center gap-1.5
              px-3 py-1.5 rounded-full
              bg-white/[0.04] hover:bg-white/[0.08]
              border border-white/[0.1] hover:border-[#7F77DD]/60
              text-[#EDF0F1]
              transition-all duration-300 relative z-50 group select-none
            "
            title="VEXL GitHub Organization"
          >
            <svg className="w-3.5 h-3.5 fill-current text-slate-300 group-hover:text-[#7F77DD] transition-colors flex-shrink-0" viewBox="0 0 24 24">
              <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.53 1.032 1.53 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
            </svg>
            <span className="font-mono text-[10px] font-bold tracking-wider uppercase text-slate-400 group-hover:text-white transition-colors">
              GitHub
            </span>
          </a>

          <Link
            to="/nova-workspace"
            className="hidden sm:flex items-center gap-1.5 px-5 py-2.5 rounded-xl liquid-glass-button-nova text-white text-[11px] font-black uppercase tracking-widest"
          >
            <span>{lang === 'en' ? 'Get Started' : 'ابدأ الآن'}</span>
          </Link>

          {/* Custom Morphing Hamburger Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-white hover:bg-white/5 rounded-lg transition-colors relative z-[110]"
          >
            <div className="w-6 h-6 flex flex-col justify-center items-center gap-1.5 overflow-hidden">
              <motion.span
                animate={{
                  rotate: isMobileMenuOpen ? 45 : 0,
                  y: isMobileMenuOpen ? 8 : 0,
                }}
                className="block w-6 h-0.5 bg-current rounded-full origin-center"
              />
              <motion.span
                animate={{
                  opacity: isMobileMenuOpen ? 0 : 1,
                  x: isMobileMenuOpen ? 20 : 0
                }}
                className="block w-6 h-0.5 bg-current rounded-full"
              />
              <motion.span
                animate={{
                  rotate: isMobileMenuOpen ? -45 : 0,
                  y: isMobileMenuOpen ? -8 : 0,
                }}
                className="block w-6 h-0.5 bg-current rounded-full origin-center"
              />
            </div>
          </button>
        </div>
      </motion.nav>

      {/* MOBILE MENU OVERLAY */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-[90] bg-[#02040a] pt-28 pb-12 px-8 lg:hidden overflow-y-auto"
          >
            {/* Background Gradient */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#7F77DD]/10 to-transparent pointer-events-none" />

            <div className="flex flex-col gap-8 relative z-10">
              {navLinks.map((link, i) => {
                const isExternal = link.href.startsWith('http');
                const className = "text-4xl font-bold text-white hover:text-[#7F77DD] transition-all flex items-center justify-between group";
                
                return isExternal ? (
                  <a key={i} href={link.href} className={className}>
                    <span>{link.label}</span>
                    <ArrowRight className="opacity-0 group-hover:opacity-100 transition-all -rotate-45" size={24} />
                  </a>
                ) : (
                  <Link key={i} to={link.href} className={className}>
                    <span>{link.label}</span>
                    <ArrowRight className="opacity-0 group-hover:opacity-100 transition-all -rotate-45" size={24} />
                  </Link>
                );
              })}

              <div className="h-px bg-white/10 my-4" />

              <Link
                to="/nova-workspace"
                className="w-full py-5 rounded-2xl liquid-glass-button-nova text-white text-center text-lg font-black uppercase tracking-widest shadow-lg flex items-center justify-center"
              >
                {lang === 'en' ? 'Launch Workspace' : 'أطلق مساحة العمل'}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default NovaNavbar;
